import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import {
  DATA_SERVICE_TOKEN,
  EoSearchPageComponent,
  SearchPageOptionsService,
  STATUS,
} from 'ngx-eo-web-components';
import { SurgeryService } from '../../../../services/surgery/surgery.service';
import { Surgery } from '../../../core/models/surgery.model';
import {
  assignedSurgerySearchFormConfig,
  assignedSurgerySearchFormViewOnlyConfig,
  initSurgerySearchForm,
  makeHospitalOptional,
  makeHospitalRequired,
  setSearchParams,
} from '../../config/surgeries-assigned-search-form.config';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AssignedSurgeriesTableConfigBuilder } from '../../config/surgeries-assigned-search.config';
import { AuthService } from '../../../../services/auth/auth.service';
import { SurgeryScheduleFormDialogComponent } from '../../../surgery/components/surgery-schedule-form-dialog/surgery-schedule-form-dialog.component';
import { HospitalAvailability } from '../../../core/models/hospital-availability.model';
import { HospitalAvailabilityService } from '../../../../services/hospitalAvailability/hospitalAvailability.service';
import { SurgeryCancelFormDialogComponent } from '../../../surgery/components/surgery-cancel-form-dialog/surgery-cancel-form-dialog.component';
import { Router } from '@angular/router';
import { is } from 'date-fns/locale';

@Component({
  selector: 'app-surgeries-assigned-list',
  templateUrl: './surgeries-assigned-list.component.html',
  styleUrl: './surgeries-assigned-list.component.scss',
  providers: [
    SearchPageOptionsService,
    { provide: DATA_SERVICE_TOKEN, useClass: SurgeryService },
  ],
})
export class SurgeriesAssignedListComponent extends EoSearchPageComponent<Surgery> {
  @ViewChild('surgeryScheduleFormDialog', { static: false })
  surgeryScheduleFormDialog?: SurgeryScheduleFormDialogComponent;

  @ViewChild('surgeryCancelFormDialog', { static: false })
  surgeryCancelFormDialog?: SurgeryCancelFormDialogComponent;

  hospitalAvailabilities: HospitalAvailability[] | null = null;

  hospitalId?: string | null;
  scheduledSurgeriesCount: number = 0;
  unScheduledSurgeriesCount: number = 0;

  data$ = new BehaviorSubject<Surgery[]>([]);
  token$ = this.authService.token$;
  tokenData = this.token$.getValue();

  clinicId = this.tokenData?.clinicId || null;

  override tableConfig = this.getSurgeryTableConfig();

  protected getSurgeryTableConfig() {
    let data = this.searchService.searchCh.data$.getValue()?.resultset ?? [];
    //console.log('data:::', data);
    return new AssignedSurgeriesTableConfigBuilder()
      .setIsViewOnly(this.isViewOnly())
      .addScheduleSurgeryFunction(this.scheduleSurgery.bind(this))
      .addReScheduleSurgeryFunction(this.reScheduleSurgery.bind(this))
      .addIsScheduleSurgeryBtnVisibleFunction(
        this.isScheduleSurgeryBtnVisible.bind(this)
      )
      .addIsReScheduleSurgeryBtnVisibleFunction(
        this.isReScheduleSurgeryBtnVisible.bind(this)
      )
      .addCancelSurgeryFunction(this.cancelSurgery.bind(this))
      .addIsCancelSurgeryBtnVisibleFunction(
        this.isCancelSurgeryBtnVisible.bind(this)
      )
      .addSurgeryStatusNameFunction(this.surgeryStatusNameCheck.bind(this))
      .addCompleteSurgeryFunction(this.completeSurgery.bind(this))
      .addIsCompleteSurgeryBtnVisibleFunction(
        this.isCompleteSurgeryBtnVisible.bind(this)
      )
      .addViewSurgeryFunction(this.viewSurgery.bind(this))
      .getTable(data);
  }

  override defaultSearchParam = {
    hospitalID: '',
    clinicID: null as string | null,
    hospitalAvailabilityId: null as string | null,
    patientAmka: null as string | null,
    status: null as string | null,
    uniqueListPatientCode: null as string | null,
  };

  override searchParam = { ...this.defaultSearchParam };
  override formConfig = assignedSurgerySearchFormConfig;

  hospitalAvailabilityListByHospitalCallHandler =
    this.hospitalAvailabilityService
      .hospitalAvailabilityListByHospitalCallHandler;

  constructor(
    override searchService: SurgeryService,
    private authService: AuthService,
    private hospitalAvailabilityService: HospitalAvailabilityService,
    private router: Router
  ) {
    super(searchService);
  }

  private surgeryStatusNameCheck(surgery?: Surgery): string {
    return surgery?.surgeryStatusAlias == 'canceled'
      ? getCanceledTooltipHtml(surgery)
      : '';
  }

  isViewOnly(): boolean {
    return this.authService.hasRoles(['ViewOnly']);
  }

  private initFormConfig(): void {
    this.formConfig = this.isViewOnly()
      ? assignedSurgerySearchFormViewOnlyConfig
      : assignedSurgerySearchFormConfig;
  }

  isToolTipVisible(): boolean {
    return (
      this.authService.hasRoles(['!ClinicAdmin']) &&
      this.authService.hasRoles(['!PrivateClinicAdmin'])
    );
  }

  isPrivateHospital(): boolean {
    return this.authService.hasRoles([
      'PrivateHospitalAdmin',
      'PrivateClinicAdmin',
    ]);
  }

  isPrivateHospitalAdmin(): boolean {
    return this.authService.hasRoles(['PrivateHospitalAdmin']);
  }

  private scheduleSurgery(surgery?: Surgery, $event?: any): void {
    this.surgeryScheduleFormDialog?.show(surgery, this.hospitalId, 'schedule');
  }

  private reScheduleSurgery(surgery?: Surgery, $event?: any): void {
    this.surgeryScheduleFormDialog?.show(
      surgery,
      this.hospitalId,
      'reschedule'
    );
  }

  private cancelSurgery(surgery?: Surgery, $event?: any): void {
    this.surgeryCancelFormDialog?.show(surgery);
  }

  private completeSurgery(surgery?: Surgery, $event?: any): void {
    if (surgery?.id) {
      this.router.navigate(['surgery/complete', surgery?.id]);
    }
  }

  private viewSurgery(surgery?: Surgery, $event?: any): void {
    if (surgery?.id) {
      this.router.navigate(['surgery/view', surgery?.id]);
    }
  }

  private isScheduleSurgeryBtnVisible(surgery?: Surgery): boolean {
    return !this.isViewOnly() && surgery?.surgeryStatusAlias.toLowerCase() === 'assigned';
  }

  private isReScheduleSurgeryBtnVisible(surgery?: Surgery): boolean {
    return !this.isViewOnly() && surgery?.surgeryStatusAlias.toLowerCase() === 'scheduled';
  }

  private isCancelSurgeryBtnVisible(surgery?: Surgery): boolean {
    const statusAlias = surgery?.surgeryStatusAlias.toLowerCase();
    return (
      !this.isViewOnly() &&
      !this.isPrivateHospital() &&
      (statusAlias === 'assigned' || statusAlias === 'scheduled')
    );
  }

  private isCompleteSurgeryBtnVisible(surgery?: Surgery): boolean {
    const statusAlias = surgery?.surgeryStatusAlias.toLowerCase();
    return !this.isViewOnly() && statusAlias === 'scheduled';
  }

  isScheduledSurgeriesLoading(): boolean {
    return (
      this.searchService.surgeryScheduledCountCallHandler.status$.getValue() ===
      STATUS.loading
    );
  }

  isUnScheduledSurgeriesLoading(): boolean {
    return (
      this.searchService.surgeryUnScheduledCountCallHandler.status$.getValue() ===
      STATUS.loading
    );
  }

  fetchScheduledSurgeriesCount(hospitalId?: string, clinicId?: string): void {
    this.searchService
      .getScheduledCount(hospitalId, clinicId)
      .subscribe((count: number) => (this.scheduledSurgeriesCount = count));
  }

  fetchUnScheduledSurgeriesCount(hospitalId?: string, clinicId?: string): void {
    this.searchService
      .getUnScheduledCount(hospitalId, clinicId)
      .subscribe((count: number) => (this.unScheduledSurgeriesCount = count));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initFormConfig();
    if (this.isViewOnly()) {
      makeHospitalOptional();
    }
    this.searchService.searchCh.data$.subscribe(() => {
      if (this.tableConfig) {
        this.tableConfig = this.getSurgeryTableConfig();
      }
    });
  }

  private initForm(clinicID: string | null, savedSearchParams?: any) {
    this.hospitalId = this.tokenData?.hospitalId || null;

    initSurgerySearchForm(
      this.hospitalId,
      clinicID,
      this.authService.hasRoles(['ClinicAdmin', 'PrivateClinicAdmin']),
      this.isViewOnly()
    );

    if (this.hospitalId || this.isViewOnly()) {
      this.getData(clinicID, savedSearchParams);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const savedSearchParams = this.searchService.getSavedSearchParams();
      if (savedSearchParams) {
        setSearchParams(savedSearchParams, this.isViewOnly());
        this.clinicId = savedSearchParams.clinicID;
        this.initForm(this.clinicId, savedSearchParams);
      } else {
        this.initForm(this.clinicId, null);
      }
    });
  }

  private getData(clinicID: string | null, savedSearchParams?: any) {
    if (!this.isPrivateHospital()) {
      this.getAllHospitalAvailabilitiesByHospital();
    }
    this.executeSearchWithParams(clinicID, savedSearchParams);
  }

  private executeSearchWithParams(
    clinicID: string | null,
    savedSearchParams: any
  ) {
    this.onSearch({
      form: {
        hospitalID: this.hospitalId,
        clinicID: clinicID,
        hospitalAvailabilityId: savedSearchParams?.hospitalAvailabilityId,
        patientAmka: savedSearchParams?.patientAmka,
        status: savedSearchParams?.status,
        uniqueListPatientCode: savedSearchParams?.uniqueListPatientCode,
      },
    });
  }

  private getAllHospitalAvailabilitiesByHospital() {
    if (!this.hospitalId) {
      this.hospitalAvailabilities = [];
      return;
    }

    return this.hospitalAvailabilityService
      .getAllHospitalAvailabilitiesByHospital(this.hospitalId ?? '')
      .subscribe((data) => {
        this.hospitalAvailabilities = data;
      });
  }

  private getAllHospitalAvailabilitiesByHospitalAndClinic() {
    if (!this.clinicId) {
      this.hospitalAvailabilities = [];
      return;
    }

    return this.hospitalAvailabilityService
      .getAllHospitalAvailabilitiesByHospitalAndClinic(
        this.hospitalId ?? '',
        this.clinicId ?? ''
      )
      .subscribe((data) => {
        this.hospitalAvailabilities = data;
      });
  }

  onSearch(searchParams?: any) {
    const savedSearchParams = this.searchService.getSavedSearchParams();

    this.searchParam =
      searchParams?.form || savedSearchParams || this.defaultSearchParam;

    //console.log("searchParam::", this.searchParam);
    this.searchService
      .searchAssignedSurgeries(
        this.searchParam,
        this.getPagesize(),
        this.getPage()
      )
      .subscribe();

    this.fetchScheduledSurgeriesCount(
      this.searchParam.hospitalID,
      this.searchParam.clinicID || undefined
    );
    this.fetchUnScheduledSurgeriesCount(
      this.searchParam.hospitalID,
      this.searchParam.clinicID || undefined
    );
  }

  override getTotalCount(): number {
    return this.searchService.searchCh.data$.getValue()?.totalCount ?? 0;
  }

  onHospitalChange($event: any) {
    this.hospitalId = $event.value || null; // Set hospitalId to selected hospital ID
  }

  onClinicChange($event: any) {
    this.clinicId = $event.value || null; // Set hospitalId to selected hospital ID
    if (this.isPrivateHospital()) {
      this.getAllHospitalAvailabilitiesByHospitalAndClinic();
    }
  }

  onClearEvent() {
    // Clear the search service
    this.searchService.searchCh.init();

    this.clinicId = this.tokenData?.clinicId || null;
    this.hospitalAvailabilities =
      this.isPrivateHospital() && this.clinicId == null
        ? []
        : this.hospitalAvailabilities;

    initSurgerySearchForm(
      this.hospitalId,
      this.clinicId,
      this.authService.hasRoles(['ClinicAdmin', 'PrivateClinicAdmin']),
      this.isViewOnly()
    );

    if (this.isViewOnly()) {
      makeHospitalOptional();
    }

    if (this.hospitalId) {
      this.onSearch({
        form: {
          hospitalID: this.hospitalId,
          clinicID: this.clinicId,
          uniqueListPatientCode: null,
        },
      });
    }
  }

  onTriggerSearch() {
    this.onSearch();
  }

  isFetchHospitalAvailabilitiesLoading(): boolean {
    return (
      this.hospitalAvailabilityListByHospitalCallHandler.status$.getValue() ===
      STATUS.loading
    );
  }
}

function getCanceledTooltipHtml(surgery: Surgery): string {
  return `<strong>Αιτιολογία</strong>: ${
    surgery?.cancelReason
  }<br/><strong>Σχόλιο</strong>: ${surgery?.cancelComments ?? '-'}`;
}
